import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { Input } from '@1trading/react/components/ui/input';
import { PopoverContent, PopoverControlled, PopoverTrigger } from '@1trading/react/components/ui/popover';
import { PseudoInputButton, PseudoInputIcon, PseudoInputLabel, PseudoInputOptions } from '@1trading/react/components/ui/pseudo-input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@1trading/react/components/ui/tooltip';
import { ChevronsUpDown, Info } from 'lucide-react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import LockedAnswersNotice from '@/components/ui/locked-answers-notice';
import { PageHeading, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import PersonalInformationFormFields from '@/features/onboarding/personal-information/components/fields';
import { useIsPersonalInformationFieldLocked } from '@/features/onboarding/personal-information/hooks/use-is-field-locked';
import type { PersonalInformationAboutYou } from '@/features/onboarding/personal-information/schema';
import { useIsPeriodicReview } from '@/features/periodic-review/context';
import { useMainstreamCountries } from '@/hooks/queries/use-mainstream-countries';
import PersonalInformationFormPersonalDetailsFieldsetDateOfBirth from './date-of-birth';

export const PersonalInformationFormFieldsetAboutYouLegend = () => {
  const { t } = useTranslation();

  return (
    <PageHeading asChild>
      <legend>
        <PageHeadingTitle>{t('personalInformation.aboutYouTitle')}</PageHeadingTitle>
        <PageHeadingDescription>{t('personalInformation.aboutYouDescription')}</PageHeadingDescription>
      </legend>
    </PageHeading>
  );
};
PersonalInformationFormFieldsetAboutYouLegend.displayName = 'PersonalInformationFormFieldsetAboutYouLegend';

export const PersonalInformationFormFieldsetAboutYou = () => {
  const form = useFormContext<PersonalInformationAboutYou>();
  const { defaultValues } = form.formState;
  const isPeriodicReview = useIsPeriodicReview();
  const isLocked = useIsPersonalInformationFieldLocked();
  const { countriesAsOptions } = useMainstreamCountries();
  const { t } = useTranslation();

  return (
    <PersonalInformationFormFields>
      <FormField
        control={form.control}
        name="firstName"
        render={({ field }) => (
          <FormItem className="col-span-full">
            <FormLabel>{t('personalInformation.firstName')}*</FormLabel>
            <FormControl>
              <Input {...field} disabled={isLocked('firstName')} placeholder={t('personalInformation.placeholders.firstName')} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="lastName"
        render={({ field }) => (
          <FormItem className="col-span-full">
            <FormLabel>{t('personalInformation.lastName')}*</FormLabel>
            <FormControl>
              <Input {...field} disabled={isLocked('lastName')} placeholder={t('personalInformation.placeholders.lastName')} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <PersonalInformationFormPersonalDetailsFieldsetDateOfBirth />
      <FormField
        control={form.control}
        name="citizenship"
        render={({ field }) => (
          <FormItem className="-sm:col-span-2">
            <div className="flex items-center gap-2">
              <FormLabel>{t('personalInformation.citizenship')}*</FormLabel>
              <TooltipProvider>
                <Tooltip delayDuration={300}>
                  <TooltipTrigger>
                    <Info className="size-4 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent>{t('personalInformation.tooltips.citizenship')}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <PopoverControlled>
              {(_, setOpen) => (
                <>
                  <PopoverTrigger disabled={isLocked('citizenship')} asChild>
                    <FormControl>
                      <PseudoInputButton role="combobox" value={field.value} {...(!field.value && { 'data-placeholder': '' })}>
                        <PseudoInputLabel>
                          {field.value ? countriesAsOptions.find((option) => option.value === field.value)?.label : t('personalInformation.placeholders.citizenship')}
                        </PseudoInputLabel>
                        <PseudoInputIcon>
                          <ChevronsUpDown />
                        </PseudoInputIcon>
                      </PseudoInputButton>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="p-0" stretch>
                    <PseudoInputOptions
                      empty={t('statuses.noResults')}
                      options={countriesAsOptions}
                      placeholder={t('fundamentals.searchCountries')}
                      value={field.value}
                      onValueChange={(value) => {
                        field.onChange(field.value === value ? defaultValues?.citizenship : value);
                        setOpen(false);
                      }}
                    />
                  </PopoverContent>
                </>
              )}
            </PopoverControlled>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="citizenship2"
        render={({ field }) => (
          <FormItem className="-sm:col-span-2">
            <FormLabel>{t('personalInformation.citizenship2')}</FormLabel>
            <PopoverControlled>
              {(_, setOpen) => (
                <>
                  <PopoverTrigger disabled={isLocked('citizenship2')} asChild>
                    <FormControl>
                      <PseudoInputButton role="combobox" value={field.value} {...(!field.value && { 'data-placeholder': '' })}>
                        <PseudoInputLabel>
                          {field.value ? countriesAsOptions.find((option) => option.value === field.value)?.label : t('personalInformation.placeholders.citizenship2')}
                        </PseudoInputLabel>
                        <PseudoInputIcon>
                          <ChevronsUpDown />
                        </PseudoInputIcon>
                      </PseudoInputButton>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="p-0" stretch>
                    <PseudoInputOptions
                      empty={t('statuses.noResults')}
                      options={countriesAsOptions}
                      placeholder={t('fundamentals.searchCountries')}
                      value={field.value}
                      onValueChange={(value) => {
                        field.onChange(field.value === value ? '' : value);
                        setOpen(false);
                      }}
                    />
                  </PopoverContent>
                </>
              )}
            </PopoverControlled>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="townOfBirth"
        render={({ field }) => (
          <FormItem className="-sm:col-span-2">
            <FormLabel>{t('personalInformation.townOfBirth')}*</FormLabel>
            <FormControl>
              <Input {...field} disabled={isLocked('townOfBirth')} placeholder={t('personalInformation.placeholders.townOfBirth')} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="countryOfBirth"
        render={({ field }) => (
          <FormItem className="-sm:col-span-2">
            <FormLabel>{t('personalInformation.countryOfBirth')}*</FormLabel>
            <PopoverControlled>
              {(_, setOpen) => (
                <>
                  <PopoverTrigger disabled={isLocked('countryOfBirth')} asChild>
                    <FormControl>
                      <PseudoInputButton role="combobox" value={field.value} {...(!field.value && { 'data-placeholder': '' })}>
                        <PseudoInputLabel>
                          {field.value ? countriesAsOptions.find((option) => option.value === field.value)?.label : t('personalInformation.placeholders.countryOfBirth')}
                        </PseudoInputLabel>
                        <PseudoInputIcon>
                          <ChevronsUpDown />
                        </PseudoInputIcon>
                      </PseudoInputButton>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="p-0" stretch>
                    <PseudoInputOptions
                      empty={t('statuses.noResults')}
                      options={countriesAsOptions}
                      placeholder={t('fundamentals.searchCountries')}
                      value={field.value}
                      onValueChange={(value) => {
                        field.onChange(field.value === value ? defaultValues?.countryOfBirth : value);
                        setOpen(false);
                      }}
                    />
                  </PopoverContent>
                </>
              )}
            </PopoverControlled>
            <FormMessage />
          </FormItem>
        )}
      />
      {isPeriodicReview && (isLocked('dateOfBirth.day') || isLocked('townOfBirth') || isLocked('countryOfBirth')) ? (
        <div className="col-span-full">
          <LockedAnswersNotice i18nKey="periodicReview.personalInformation.locked" />
        </div>
      ) : null}
    </PersonalInformationFormFields>
  );
};
PersonalInformationFormFieldsetAboutYou.displayName = 'PersonalInformationFormFieldsetAboutYou';
